import React, { forwardRef, useState, useImperativeHandle, useRef } from 'react';
import { tr } from '../../../i18n/i18n';
import { useTranslation } from 'react-i18next';
import { ModalHandle } from '../';
import ConfirmationModal from '../ConfirmationModal';
import { AppTextarea } from '../../atoms';

interface RevokePaymentModalProps {
  // pass the selected assign_invoice_id so modal can include it in the revoke call
  assignInvoiceId?: string | null;
  onRevoke: (assign_invoice_id: string, revocation_note: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const RevokePaymentModal = forwardRef<ModalHandle, RevokePaymentModalProps>(
  ({ assignInvoiceId, onRevoke, onCancel, isLoading = false }, ref) => {
    const { t } = useTranslation();
    const modalRef = useRef<ModalHandle>(null);
    const [revocationNote, setRevocationNote] = useState('');
    const [error, setError] = useState('');

    // Expose modal methods to parent
    useImperativeHandle(ref, () => ({
      openModal: () => {
        setRevocationNote('');
        setError('');
        modalRef.current?.openModal();
      },
      closeModal: () => {
        setRevocationNote('');
        setError('');
        modalRef.current?.closeModal();
      },
    }));

    const handleRevoke = () => {
      // Require a non-empty revocation note; do not enforce a minimum length
      if (!revocationNote.trim()) {
        setError(tr('g.revocation_note_is_mandatory', 'Revocation note is mandatory'));
        return;
      }

      if (revocationNote.length > 150) {
        setError(
          tr(
            'g.revocation_note_cannot_exceed_150_characters',
            'Revocation note cannot exceed 150 characters',
          ),
        );
        return;
      }

      onRevoke(assignInvoiceId || '', revocationNote.trim());
    };

    const handleCancel = () => {
      setRevocationNote('');
      setError('');
      onCancel();
    };

    const handleNoteChange = (value: string) => {
      setRevocationNote(value);
      if (error) setError(''); // Clear error on input change
    };

    // Modal is specific to revoke payment; use danger styling by default

    // Build title with invoice id if available (avoid duplicate #)
    const displayInvoice = assignInvoiceId
      ? assignInvoiceId.startsWith('#')
        ? assignInvoiceId
        : `#${assignInvoiceId}`
      : '';

    const title = `Revoke Payment for Invoice ${displayInvoice || ''}${displayInvoice ? '?' : ''}`;

    const content = (
      <div className="flex flex-col items-center justify-center gap-4 p-4">
        {/* Description */}
        <div className="text-sm font-normal text-center font-poppins text-gray-600">
          {t(
            'fe_billing.confirm_revoke_manual_payment',
            'Are you sure you want to revoke this manual payment? Please provide a reason for this revocation.',
          )}
        </div>

        {/* Textarea for Revocation Note */}
        <div className="w-full">
          <AppTextarea
            value={revocationNote}
            onChange={(e) => handleNoteChange(e.target.value)}
            placeholder={t(
              'common.placeholder_enter_revocation_reason',
              'Enter reason for revocation (required, max 150 characters)',
            )}
            rows={4}
            maxLength={150}
            className={`w-full ${error ? 'border-red-500' : ''}`}
            disabled={isLoading}
          />
          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          <p className="text-gray-500 text-xs mt-1 text-right">
            {t('common.characters_count_150', '{{current}}/150 characters', {
              current: revocationNote.length,
            })}
          </p>
        </div>
      </div>
    );

    return (
      <ConfirmationModal
        ref={modalRef}
        title={title}
        content={content}
        icon="pi-ban"
        width="500px"
        severity="danger"
        buttonSeverity="danger"
        yesLabel={tr('fe_modals.yes_revoke_payment', 'Yes, Revoke Payment')}
        noLabel={t('common.button_cancel')}
        yesDisabled={isLoading || !revocationNote.trim()}
        onYes={handleRevoke}
        onNo={handleCancel}
      />
    );
  },
);

RevokePaymentModal.displayName = 'RevokePaymentModal';

export default RevokePaymentModal;
